/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { formatDistanceToNow } from 'date-fns';
/* eslint-disable import/no-anonymous-default-export */
import { OptionsObject, SnackbarMessage, useSnackbar, WithSnackbarProps } from 'notistack';
import React from 'react';

import { throttleWithBurst } from '../utils';
import './SnackBarUtils.scss';

// Must be imported at least once in the app to initialize the ref
let snackbarRef: WithSnackbarProps;
export const SnackbarUtilsConfigurator: React.FC = () => {
  snackbarRef = useSnackbar();
  return null;
};

type InfoTopType = 'LOCK_MESSAGE' | 'NEW_APP_VERSION' | 'HOT_POTATO';
const infoTopSnackKey: { [type: string]: string | number | undefined } = {};
type WarningTopType = 'EDITING_PUBLISHED';
const warningTopSnackKey: { [type: string]: string | number | undefined } = {};
type ErrorTopType = 'JSON_PARSE_ERROR' | 'EDITING_PUBLISHED_WITH_DRAFT' | 'PAGE_MATCH_ERROR';
const errorTopSnackKey: { [type: string]: string | number | undefined } = {};

/* Note, the duration and max number of snacks is configured in App.tsx
   SnackbarProvider */
export const snackNotifications = {
  success(msg: SnackbarMessage, options: OptionsObject = {}): void {
    this.toast(msg, { anchorOrigin: { vertical: 'top', horizontal: 'left' }, ...options, variant: 'success' });
  },
  warning(msg: SnackbarMessage, options: OptionsObject = {}): void {
    this.toast(msg, { anchorOrigin: { vertical: 'top', horizontal: 'left' }, ...options, variant: 'warning' });
  },
  info(msg: SnackbarMessage, options: OptionsObject = {}): void {
    this.toast(msg, { anchorOrigin: { vertical: 'top', horizontal: 'left' }, ...options, variant: 'info' });
  },
  infoTopClear({ type }: { type: InfoTopType }): void {
    if (infoTopSnackKey[type]) {
      snackbarRef?.closeSnackbar(infoTopSnackKey[type]);
      delete infoTopSnackKey[type];
    }
  },
  warningTopClear({ type }: { type: WarningTopType }): void {
    if (warningTopSnackKey[type]) {
      snackbarRef?.closeSnackbar(warningTopSnackKey[type]);
      delete warningTopSnackKey[type];
    }
  },
  errorTopClear({ type }: { type: ErrorTopType }): void {
    if (errorTopSnackKey[type]) {
      snackbarRef?.closeSnackbar(errorTopSnackKey[type]);
      delete errorTopSnackKey[type];
    }
  },
  topClearAll(): void {
    Object.keys(infoTopSnackKey).forEach((key) => {
      snackbarRef?.closeSnackbar(infoTopSnackKey[key]);
      delete infoTopSnackKey[key];
    });
    Object.keys(warningTopSnackKey).forEach((key) => {
      snackbarRef?.closeSnackbar(warningTopSnackKey[key]);
      delete warningTopSnackKey[key];
    });
    Object.keys(errorTopSnackKey).forEach((key) => {
      snackbarRef?.closeSnackbar(errorTopSnackKey[key]);
      delete errorTopSnackKey[key];
    });
  },
  infoTop(msg: SnackbarMessage, options: OptionsObject & { type: InfoTopType }): void {
    this.infoTopClear({ type: options.type });
    const ret = this.toast(msg, {
      preventDuplicate: true,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      variant: 'info',
      ...options,
    });
    if (ret) {
      // ret can be undefined when rate limiting kicks in
      infoTopSnackKey[options.type] = ret;
    }
  },
  warningTop(msg: SnackbarMessage, options: OptionsObject & { type: WarningTopType }): void {
    this.warningTopClear({ type: options.type });
    const ret = this.toast(msg, {
      preventDuplicate: true,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      variant: 'warning',
      ...options,
    });
    if (ret) {
      // ret can be undefined when rate limiting kicks in
      warningTopSnackKey[options.type] = ret;
    }
  },
  errorTop(msg: SnackbarMessage, options: OptionsObject & { type: ErrorTopType }): void {
    this.errorTopClear({ type: options.type });
    const ret = this.toast(msg, {
      preventDuplicate: true,
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      variant: 'error',
      ...options,
    });
    if (ret) {
      // ret can be undefined when rate limiting kicks in
      errorTopSnackKey[options.type] = ret;
    }
  },
  error(msg: SnackbarMessage, options: OptionsObject = {}): void {
    this.toast(msg, { anchorOrigin: { vertical: 'top', horizontal: 'left' }, ...options, variant: 'error' });
  },
  toast: throttleWithBurst(
    (msg: SnackbarMessage, options: OptionsObject = {}) => {
      // can be undefined in jest tests
      return snackbarRef?.enqueueSnackbar(msg, options);
    },
    500,
    3 /* burst */
  ),
  locked(lockedBy: string, lockWhen: string): void {
    const lockWhenStr = formatDistanceToNow(new Date(lockWhen), { addSuffix: true });
    snackNotifications.infoTop(`Locked by ${lockedBy}, ${lockWhenStr}`, { persist: true, type: 'LOCK_MESSAGE' });
  },
};
